/**
 * The settings of a group's repeat (a card of its modifier stack): copies in a
 * row, around a point, or placed by a rule (AI-made, read-only here).
 */

import type { ObjectGroup, ObjectLinearRepeat, ObjectRadialRepeat, ObjectRepeatSkip } from 'shared-types';
import { shapesBounds } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { T, useT } from '../../../translation/src';
import { defaultLinearRepeat, roundCoord } from '../document/specOps';
import { useEditorState } from '../EditorContext';
import { FieldRow, NumberField, PanelButton, PointFields, Segmented, SelectField, StackedFieldRow, ToggleRow } from '../panels/fields';
import { BindingField } from './BindingField';

const DEG = 180 / Math.PI;

export const LinearRepeatFields: React.FC<{ group: ObjectGroup; repeat: ObjectLinearRepeat; update: (r: ObjectLinearRepeat) => void }> = ({ group, repeat, update }) => {
  const { spec } = useEditorState();
  const numeric = spec.params.filter((p) => p.kind === 'float' || p.kind === 'int');
  const counts = spec.params.filter((p) => p.kind === 'int');
  const autoCount = useT('from the spacing', { swc: true });
  const skipLabels = {
    none: useT('keep all', { swc: true }),
    first: useT('drop the first', { swc: true }),
    last: useT('drop the last', { swc: true }),
    both: useT('drop first and last', { swc: true }),
  } satisfies Record<ObjectRepeatSkip, string>;
  const fitToGroup = () => {
    const shapes = spec.shapes.filter((s) => group.shapeIds.includes(s.id));
    const b = shapesBounds(shapes, repeat.axis === 'u' ? 0 : 1);
    if (b.max - b.min > 0.001) update({ ...repeat, cell: [roundCoord(b.min), roundCoord(b.max)] });
  };
  return (
    <>
      <FieldRow label={<T _str="direction" swc />}>
        <Segmented
          value={repeat.axis}
          options={[
            { value: 'u', label: <T _str="width" swc /> },
            { value: 'n', label: <T _str="depth" swc /> },
          ]}
          onChange={(axis) => {
            // New cell along the other axis; parameter bindings carry over, a typed-in spacing is recomputed.
            const next = defaultLinearRepeat(spec, group, axis);
            update({ ...next, ...(repeat.pitch && 'param' in repeat.pitch ? { pitch: repeat.pitch } : {}), ...(repeat.count ? { count: repeat.count } : {}), ...(repeat.skip ? { skip: repeat.skip } : {}) });
          }}
        />
      </FieldRow>
      <StackedFieldRow
        label={<T _str="one cell" swc />}
        hint={<T _str="the part of the drawing that is one step, tread or module (the shaded band): where it starts and ends" swc />}
        action={
          <PanelButton onClick={fitToGroup}>
            <T _str="fit to the group" swc />
          </PanelButton>
        }
      >
        <span className="grid grid-cols-2 gap-1">
          <NumberField value={repeat.cell[0]} unit="m" onCommit={(a) => a < repeat.cell[1] && update({ ...repeat, cell: [a, repeat.cell[1]] })} />
          <NumberField value={repeat.cell[1]} unit="m" onCommit={(b) => b > repeat.cell[0] && update({ ...repeat, cell: [repeat.cell[0], b] })} />
        </span>
      </StackedFieldRow>
      <FieldRow label={<T _str="spacing" swc />}>
        <BindingField binding={repeat.pitch} params={numeric} unit="m" fallback={roundCoord(repeat.cell[1] - repeat.cell[0])} onChange={(pitch) => update({ ...repeat, pitch })} allowNone={repeat.count ? '—' : undefined} />
      </FieldRow>
      <FieldRow label={<T _str="count" swc />} hint={repeat.pitch && repeat.count && 'param' in repeat.pitch && 'param' in repeat.count ? <T _str="spacing and count stay in sync on placed objects" swc /> : undefined}>
        <BindingField binding={repeat.count} params={counts} integer fallback={3} onChange={(count) => update({ ...repeat, count })} allowNone={repeat.pitch ? autoCount : undefined} />
      </FieldRow>
      <ToggleRow label={<T _str="stretch cells to fill the run" swc />} checked={repeat.fit === 'fill'} onChange={(on) => update({ ...repeat, fit: on ? 'fill' : 'exact' })} />
      <ToggleRow
        label={<T _str="scale cell contents" swc />}
        hint={<T _str="off: contents keep their size, centered in their cell (chairs)" swc />}
        checked={repeat.cellResize === 'stretch'}
        onChange={(on) => update({ ...repeat, cellResize: on ? 'stretch' : 'fixed' })}
      />
      <ToggleRow label={<T _str="mirror every other cell" swc />} hint={<T _str="alternating door hinges" swc />} checked={!!repeat.mirrorAlternate} onChange={(mirrorAlternate) => update({ ...repeat, mirrorAlternate })} />
      <FieldRow label={<T _str="ends" swc />}>
        <SelectField value={repeat.skip ?? 'none'} options={(['none', 'first', 'last', 'both'] as const).map((v) => ({ value: v, label: skipLabels[v] }))} onChange={(skip) => update({ ...repeat, skip })} />
      </FieldRow>
    </>
  );
};

export const RadialRepeatFields: React.FC<{ repeat: ObjectRadialRepeat; update: (r: ObjectRadialRepeat) => void }> = ({ repeat, update }) => {
  const { spec } = useEditorState();
  const counts = spec.params.filter((p) => p.kind === 'int');
  return (
    <>
      <StackedFieldRow label={<T _str="center" swc />} hint={<T _str="from the object's center: X to the right, Y up" swc />}>
        <PointFields value={repeat.center} onCommit={(center) => update({ ...repeat, center })} />
      </StackedFieldRow>
      <FieldRow label={<T _str="count" swc />}>
        <BindingField binding={repeat.count} params={counts} integer fallback={8} onChange={(count) => count && update({ ...repeat, count })} />
      </FieldRow>
      <FieldRow label={<T _str="start angle" swc />}>
        <NumberField value={repeat.startAngle * DEG} unit="°" decimals={1} onCommit={(a) => update({ ...repeat, startAngle: a / DEG })} />
      </FieldRow>
      <FieldRow label={<T _str="turn" swc />}>
        <NumberField value={repeat.sweep * DEG} unit="°" decimals={1} min={-360} max={360} onCommit={(a) => update({ ...repeat, sweep: a / DEG })} />
      </FieldRow>
    </>
  );
};

/** Copies placed by a rule (made by a part or the AI): shown as what they are, removed from the Rules list. */
export const IndexRepeatNote: React.FC = () => (
  <p className="text-xs text-gray-500 dark:text-gray-400">
    <T _str="copies placed by a rule; remove the rule in the Rules list to turn them into plain shapes" swc />
  </p>
);
